import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';

import * as TimSort from 'timsort';

import { Action, ContextAction, filterRegularActions } from '../action.model';
import { ContextMenuService } from '../context-menu/context-menu.service';
import { PageChangeEvent } from '../controls/paginator/paginator.component';
import { LocaleService } from '../locale/locale.service';
import { LocalizedModel } from '../locale/localized-model';
import { LocalizedModelService } from '../locale/localized-model.service';
import { compareByLabel, compareByValue, partialMatches, isLabelledValue } from '../util/compare';
import { Subscription } from '../util/rxjs';
import { TableBodyComponent } from './body/table-body.component';
import { ColumnComponent } from './column.component';
import { InlineAction, TableRow, TableSortKey } from './shared';

@Component({
  selector: 'cp-table',
  templateUrl: './table.component.html',
  styleUrls: [
    './table.component.less',
    './themes/regular.less',
    './themes/dashboard.less',
    './themes/small.less'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class TableComponent implements AfterContentInit, OnChanges, OnInit, OnDestroy {
  @Input() theme: string;

  @Input() autoColumns?: boolean;

  @Input() actions?: Array<ContextAction<TableRow>>;

  @Input() hideHeaders?: boolean;

  @Input() hideSelection?: boolean;

  @Input() hideActions = false;

  @Input() filter?: boolean;

  @Input() dropdownFilter: string;

  @Input() pagination?: number;

  @Input() dropdownFilterPlaceholder: string;

  @Input() rowClass?: string;

  @Input() panel = false;

  @Input() rows: TableRow[];

  @Input() tooltip: TemplateRef<any>;

  @Output() rowClick = new EventEmitter<TableRow>();

  @ContentChild('secondaryRow') secondaryRow: TemplateRef<any>;

  @ContentChild('caption') caption: TemplateRef<any>;

  @ContentChildren(ColumnComponent, {descendants: true}) columns: QueryList<ColumnComponent>;

  inlineActions?: Array<Action<TableRow>>;
  localizedModel: LocalizedModel<TableRow>;

  visibleRows?: TableRow[];
  filterValue: string;
  dropdownFilterOptions?: any[];

  private filterSubscription: Subscription;
  private dropdownFilterValue: any[];

  private startIndex = 0;
  private endIndex?: number;

  private sortedBy: TableSortKey = { key: undefined, descending: false };
  private selection = new Map<string | number, TableRow>();
  private visibleSelection = new Map<string | number, TableRow>();

  get modelRows() {
    return this.localizedModel.data;
  }

  get tableClass() {
    return `table table-${this.theme}`;
  }

  get isAllSelected() {
    if (!this.visibleRows || this.visibleRows.length !== this.visibleSelection.size) {
      return false;
    }

    return this.visibleRows.every(row => this.visibleSelection.has(row.key));
  }

  get showPaginator() {
    return !!this.pagination && !!this.modelRows && this.modelRows.length > this.pagination;
  }

  constructor(
    private modelTranslator: LocalizedModelService,
    private localeService: LocaleService,
    private contextMenu: ContextMenuService,
    private cd: ChangeDetectorRef
  ) {
    this.localizedModel = new LocalizedModel<TableRow>(this.localeService, this.modelTranslator, {
      exclude: value => {
        if (
          !this.dropdownFilter ||
          !this.dropdownFilterValue ||
          this.dropdownFilterValue.length === 0
        ) {
          return false;
        }

        return this.isExcluded(value);
      },
      sort: data => {
        TimSort.sort(data, this.tableSortFunction);
        return data;
      },
      filter: value => this.isIncluded(value)
    });
  }

  ngOnInit() {
    this.endIndex = this.pagination;
    this.localizedModel.onUpdate(this.rows);
    this.inlineActions = filterRegularActions(this.actions);
    this.updatePaginatedRows();
  }

  ngAfterContentInit() {
    this.preprocessRows();
    this.cd.detectChanges();
  }

  ngOnDestroy() {
    if (this.localizedModel) {
      this.localizedModel.onDestroy();
    }

    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const rows = changes['rows'];
    const actions = changes['actions'];
    if (rows) {
      this.preprocessRows();
      this.localizedModel.onUpdate(rows.currentValue);
      this.updateDropdownFilterOptions(rows.currentValue);
      this.pruneSelections();
      this.updatePaginatedRows();
    }
    if (actions) {
      this.inlineActions = filterRegularActions(actions.currentValue);
    }
  }

  identify(_index: number, item: TableRow) {
    return item.key;
  }

  onPageChange(event: PageChangeEvent) {
    this.startIndex = event.startIndex;
    this.endIndex = event.endIndex;
    this.updatePaginatedRows();
    this.pruneVisibleSelections();
  }

  onDropdownFilterChange(value: any[]) {
    if (value !== this.dropdownFilterValue) {
      this.dropdownFilterValue = value;
      this.localizedModel.onUpdateExisting();
      this.pruneVisibleSelections();
      this.updatePaginatedRows();
    }
  }

  onFilterChange(value: string) {
    this.filterValue = value;
    this.localizedModel.onUpdateExisting();
    this.pruneVisibleSelections();
    this.updatePaginatedRows();
  }

  onSort(column: ColumnComponent) {
    if (!column.sortable || !column.field) {
      return;
    }

    if (this.sortedBy.key !== column) {
      this.sortedBy = { key: column, descending: false };
    } else {
      this.sortedBy.descending = !this.sortedBy.descending;
    }
    this.localizedModel.onSort();
    this.updatePaginatedRows();
    this.cd.detectChanges();
  }

  onContextMenu(row: TableRow, host: TableBodyComponent, $event: MouseEvent) {
    if (!this.actions) {
      return;
    }

    if (!this.isSelected(row) && !this.hideActions) {
      this.selection = new Map([[row.key, row]]);
      this.visibleSelection = new Map([[row.key, row]]);
    }

    this.contextMenu.show($event, host.element.nativeElement, this.actions, row);
  }

  onActionClicked(inlineAction: InlineAction<TableRow>) {
    const { row, action } = inlineAction;
    if (!this.isSelected(row)) {
      this.selection = new Map([[row.key, row]]);
      this.visibleSelection = new Map([[row.key, row]]);
    }

    if (action.type === 'executed') {
      action.execute(row, Array.from(this.visibleSelection.values()));
    }
  }

  isSelected(row: TableRow) {
    return this.selection.has(row.key);
  }

  toggleSelection(row: TableRow) {
    if (this.isSelected(row)) {
      this.selection.delete(row.key);
      this.visibleSelection.delete(row.key);
    } else {
      this.selection.set(row.key, row);
      this.visibleSelection.set(row.key, row);
    }
  }

  toggleAll() {
    if (!this.visibleRows) {
      return;
    }

    this.selection = new Map(this.selection);
    this.visibleSelection = new Map(this.visibleSelection);

    const hasSelection = this.visibleRows.some(row => this.selection.has(row.key));
    if (hasSelection) {
      for (const row of this.visibleRows) {
        this.selection.delete(row.key);
        this.visibleSelection.delete(row.key);
      }
    } else {
      for (const row of this.visibleRows) {
        this.selection.set(row.key, row);
        this.visibleSelection.set(row.key, row);
      }
    }
  }

  onRowClick(row: TableRow) {
    if (window && window.getSelection && window.getSelection()?.toString()) {
      return;
    }
    this.rowClick.emit(row);
  }

  private preprocessRows() {
    if (!this.rows || !this.columns) {
      return;
    }
    for (const row of this.rows) {
      row.$$fields = {};

      this.columns.forEach(column => {
        if (column.field && !row.$$fields[column.field]) {
          row.$$fields[column.field] = this.getField(row, column);
        }
      });
    }
  }

  private getField(row: TableRow, column: ColumnComponent) {
    return column.field && this.resolveFieldByName(row, column.field);
  }

  private resolveFieldByName(row: TableRow, fieldName: string) {
    const path = fieldName.split('.');
    if (path.length === 1) {
      return row[path[0]];
    }
    return path.reduce((current, field) => current[field], row);
  }

  private updateDropdownFilterOptions(rows?: TableRow[]) {
    if (!this.dropdownFilter) {
      return;
    }

    this.dropdownFilterOptions =
      rows &&
      Array.from(
        new Map(
          rows.map<[any, any]>(row => {
            const field = this.resolveFieldByName(row, this.dropdownFilter);
            const key = isLabelledValue(field) ? field.label : field;
            return [key, field];
          })
        ).values()
      ).sort(compareByLabel);
  }

  private get tableSortFunction(): ((a: TableRow, b: TableRow) => number) | undefined {
    if (!this.sortedBy.key) {
      return undefined;
    }

    const column = this.sortedBy.key;
    const descending = this.sortedBy.descending;

    if (!column.field) {
      return undefined;
    }

    return column.sortByLabel
      ? (a, b) =>
          compareByLabel(a.$$fields[column.field!], b.$$fields[column.field!], { descending })
      : (a, b) =>
          compareByValue(a.$$fields[column.field!], b.$$fields[column.field!], { descending });
  }

  private get isIncluded() {
    return (row: TableRow) =>
      !this.filterValue ||
      this.columns.some(
        column =>
          !!column.filter &&
          partialMatches(row.$$fields[column.field!], this.filterValue, { ignoreCase: true })
      );
  }

  private get isExcluded() {
    return (row: TableRow) => {
      return (
        compareByValue(
          this.dropdownFilterValue[0].value,
          this.resolveFieldByName(row, this.dropdownFilter)
        ) !== 0
      );
    };
  }

  private pruneSelections() {
    if (this.selection.size === 0) {
      return;
    }

    if (!this.rows || this.rows.length === 0) {
      this.selection = new Map();
      this.visibleSelection = new Map();
      return;
    }

    const newSelection = new Map<string | number, TableRow>();
    const newVisibleSelection = new Map<string | number, TableRow>();

    const visibleRows = new Set(this.localizedModel.data!.map(v => v.key));
    for (const row of this.rows) {
      if (this.selection.has(row.key)) {
        newSelection.set(row.key, row);
        if (visibleRows.has(row.key)) {
          newVisibleSelection.set(row.key, row);
        }
      }
    }

    this.selection = newSelection;
    this.visibleSelection = newVisibleSelection;
  }

  private pruneVisibleSelections() {
    if (this.selection.size === 0) {
      return;
    }

    if (!this.rows || this.rows.length === 0) {
      this.visibleSelection = new Map();
      return;
    }

    this.visibleSelection = new Map(
      this.visibleRows!
        .filter(row => this.isSelected(row))
        .map(row => [row.key, row] as [string | number, TableRow])
    );
  }

  private updatePaginatedRows() {
    this.visibleRows =
      this.modelRows &&
      this.modelRows.slice(this.startIndex, this.endIndex || this.modelRows.length);
  }
}
